<div class="bg-white rounded-xl shadow-2xl w-full overflow-hidden flex flex-col max-h-[86vh]">
  <!-- En-tête : centre courant, vue, recherche -->
  <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-200 shrink-0">
    <button
      type="button"
      (click)="back()"
      [disabled]="history().length === 0"
      title="Centre précédent (Retour arrière)"
      aria-label="Centre précédent"
      class="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-gray-500 transition-colors duration-150 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer disabled:cursor-default focus:outline-none focus:ring-2 focus:ring-[#993225]"
    >
      <fa-icon [icon]="icons.faArrowLeft" class="text-[15px] leading-none" aria-hidden="true"></fa-icon>
    </button>

    <div class="flex flex-col min-w-0 flex-1">
      <span class="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Graphe du propriétaire</span>
      @if (anchorNode(); as node) {
        <span class="text-sm font-medium text-gray-900 truncate">
          {{ kindName(node.kind) }} · {{ cardLabel(node) }}
        </span>
      } @else if (!searching()) {
        <span class="text-sm text-gray-500">—</span>
      } @else {
        <span class="text-sm text-gray-500">Choisissez une fiche</span>
      }
    </div>

    @if (graph()) {
      <div class="flex shrink-0 rounded-lg border border-gray-200 p-0.5 bg-gray-50 text-xs font-medium" role="group" aria-label="Vue">
        <button
          type="button"
          (click)="setView('local')"
          [attr.aria-pressed]="view() === 'local'"
          class="px-2.5 py-1 rounded-md transition-colors duration-150 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#993225]"
          [class.bg-white]="view() === 'local'"
          [class.shadow-sm]="view() === 'local'"
          [class.text-gray-900]="view() === 'local'"
          [class.text-gray-500]="view() !== 'local'"
        >Autour de la fiche</button>
        <button
          type="button"
          (click)="setView('full')"
          [disabled]="!canToggleView()"
          [attr.aria-pressed]="view() === 'full'"
          [title]="canToggleView() ? 'Tout le propriétaire (T)' : 'La fiche est un propriétaire : tout son graphe est déjà affiché'"
          class="px-2.5 py-1 rounded-md transition-colors duration-150 cursor-pointer disabled:cursor-default disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-[#993225]"
          [class.bg-white]="view() === 'full'"
          [class.shadow-sm]="view() === 'full'"
          [class.text-gray-900]="view() === 'full'"
          [class.text-gray-500]="view() !== 'full'"
        >Tout le propriétaire</button>
      </div>
    }

    <button
      type="button"
      (click)="openSearch()"
      title="Changer de fiche (/)"
      aria-label="Changer de fiche"
      class="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-gray-500 transition-colors duration-150 hover:bg-gray-100 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#993225]"
    >
      <fa-icon [icon]="icons.faMagnifyingGlass" class="text-[15px] leading-none" aria-hidden="true"></fa-icon>
    </button>
  </div>

  <!-- Recherche d'une fiche de départ -->
  @if (searching()) {
    <div class="border-b border-gray-200 shrink-0">
      <div class="flex items-center gap-3 px-4">
        <fa-icon [icon]="icons.faMagnifyingGlass" class="text-gray-400 text-base shrink-0" aria-hidden="true"></fa-icon>
        <input
          #searchInput
          type="text"
          role="combobox"
          aria-label="Rechercher la fiche à placer au centre du graphe"
          aria-controls="owner-graph-search-listbox"
          aria-autocomplete="list"
          [attr.aria-expanded]="hits().length > 0"
          [attr.aria-activedescendant]="hits().length ? 'owner-graph-search-' + searchActive() : null"
          autocomplete="off"
          spellcheck="false"
          placeholder="Bail, propriétaire, locataire ou bien à placer au centre…"
          [value]="query()"
          (input)="onSearchInput($any($event.target).value)"
          (keydown)="onSearchKeydown($event)"
          class="flex-1 min-w-0 py-3.5 text-base text-gray-900 bg-transparent border-0 focus:outline-none focus:ring-0"
        />
        @if (searchLoading()) {
          <span class="w-4 h-4 shrink-0 rounded-full border-2 border-gray-200 border-t-[#993225] animate-spin" role="status" aria-label="Recherche en cours"></span>
        }
      </div>
      <div id="owner-graph-search-listbox" role="listbox" aria-label="Fiches" class="max-h-[40vh] overflow-y-auto">
        @if (query().trim().length < minLength) {
          <p class="px-4 pb-4 text-sm text-gray-500">
            @if (!graph()) { Aucune fiche ouverte. } Tapez au moins {{ minLength }} caractères.
          </p>
        }
        @for (hit of hits(); track hit.kind + hit.id; let i = $index) {
          <button
            type="button"
            [id]="'owner-graph-search-' + i"
            role="option"
            [attr.aria-selected]="i === searchActive()"
            (click)="pickHit(hit)"
            (mousemove)="searchActive.set(i)"
            tabindex="-1"
            class="w-full flex items-center gap-3 px-4 py-2 text-left cursor-pointer transition-colors duration-100"
            [class.bg-primary-50]="i === searchActive()"
          >
            <fa-icon [icon]="icon(hit.kind)" class="text-gray-500 w-5 text-center" aria-hidden="true"></fa-icon>
            <span class="flex flex-col min-w-0 flex-1">
              <span class="text-sm font-medium text-gray-900 truncate">{{ hit.label }}</span>
              @if (hit.sublabel) { <span class="text-xs text-gray-500 truncate">{{ hit.sublabel }}</span> }
            </span>
            <span class="shrink-0 text-[11px] text-gray-400">{{ kindName(hit.kind) }}</span>
          </button>
        }
      </div>
    </div>
  }

  <!-- Graphe -->
  <div
    #canvas
    tabindex="0"
    role="group"
    aria-label="Graphe du propriétaire. Flèches pour se déplacer, Entrée pour ouvrir, Espace pour recentrer."
    [attr.aria-activedescendant]="focusKey() ? cardId(focusKey()!) : null"
    (keydown)="onCanvasKeydown($event)"
    class="relative flex-1 min-h-[220px] overflow-auto bg-[#f6f8fc] focus:outline-none"
  >
    @if (error()) {
      <p class="p-8 text-sm text-red-700 text-center">Le graphe n'a pas pu être chargé. Réessayez dans un instant.</p>
    } @else if (layout(); as l) {
      <div class="relative m-6 transition-opacity duration-150" [class.opacity-50]="loading()" [style.width.px]="l.width" [style.height.px]="l.height">
        @for (column of l.columns; track column.title) {
          <div class="absolute top-0 text-[11px] font-semibold uppercase tracking-wider text-gray-400" [style.left.px]="column.x">
            {{ column.title }}
          </div>
        }

        <svg class="absolute inset-0 overflow-visible pointer-events-none" [attr.width]="l.width" [attr.height]="l.height" aria-hidden="true">
          @for (edge of edgeClasses(); track edge.from + edge.to) {
            <path
              [attr.d]="edge.path"
              fill="none"
              [attr.stroke]="edge.active ? '#993225' : '#cbd5e1'"
              [attr.stroke-width]="edge.active ? 2 : 1.5"
            />
          }
        </svg>

        @for (card of l.cards; track card.key) {
          @if (card.fold; as fold) {
            <button
              type="button"
              [id]="cardId(card.key)"
              (click)="toggleFold(fold.propertyKey)"
              class="absolute flex items-center px-3 rounded-lg border border-dashed border-gray-300 bg-white/60 text-xs font-medium text-gray-500 cursor-pointer transition-colors duration-150 hover:bg-white hover:text-gray-700"
              [class.ring-2]="card.key === focusKey()"
              [class.ring-primary-300]="card.key === focusKey()"
              [style.left.px]="card.x"
              [style.top.px]="card.y"
              [style.width.px]="cardW"
              [style.height.px]="cardH"
            >
              ⋯ {{ fold.count }} {{ fold.count > 1 ? 'baux terminés' : 'bail terminé' }}
            </button>
          } @else if (card.node; as node) {
            <div
              class="group absolute"
              [style.left.px]="card.x"
              [style.top.px]="card.y"
              [style.width.px]="cardW"
              [style.height.px]="cardH"
              (mouseenter)="focusKey.set(card.key)"
            >
              <a
                [id]="cardId(card.key)"
                [routerLink]="link(node)"
                (click)="onCardClick($event, card)"
                tabindex="-1"
                [title]="node.kind === 'lease' ? node.label : ''"
                class="w-full h-full flex items-center gap-2.5 pl-2.5 pr-8 rounded-lg border bg-white shadow-sm cursor-pointer transition-all duration-150 hover:shadow-md"
                [class.border-gray-200]="!card.anchor && !node.external"
                [class.border-dashed]="node.external"
                [class.border-gray-300]="node.external"
                [class.border-primary-800]="card.anchor"
                [class.ring-2]="card.key === focusKey() || card.anchor"
                [class.ring-primary-100]="card.anchor && card.key !== focusKey()"
                [class.ring-primary-300]="card.key === focusKey()"
                [class.opacity-60]="node.ended || node.archived"
              >
                <span
                  class="w-8 h-8 shrink-0 rounded-md flex items-center justify-center"
                  [class.bg-primary-50]="card.anchor"
                  [class.text-primary-800]="card.anchor"
                  [class.bg-gray-100]="!card.anchor"
                  [class.text-gray-500]="!card.anchor"
                >
                  <fa-icon [icon]="icon(node.kind)" class="text-[15px] leading-none" aria-hidden="true"></fa-icon>
                </span>
                <span class="flex flex-col min-w-0 flex-1">
                  <span class="text-[13px] font-medium text-gray-900 truncate" [class.tabular-nums]="node.kind === 'lease'">{{ cardLabel(node) }}</span>
                  @if (cardSublabel(node); as sub) {
                    <span class="text-[11px] text-gray-500 truncate">{{ sub }}</span>
                  }
                </span>
                @if (node.archived) {
                  <span class="shrink-0 px-1 py-0.5 text-[10px] font-medium text-gray-500 bg-gray-100 rounded">Archivé</span>
                }
              </a>
              @if (!card.anchor && !node.external) {
                <!-- Frère du lien et non enfant : un bouton dans un <a> n'est pas du HTML valide. -->
                <button
                  type="button"
                  (click)="recenter($event, node)"
                  title="Centrer le graphe ici (Espace)"
                  aria-label="Centrer le graphe ici"
                  tabindex="-1"
                  class="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md flex items-center justify-center text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-150 hover:bg-gray-100 hover:text-gray-700 cursor-pointer"
                >
                  <fa-icon [icon]="icons.faCrosshairs" class="text-[13px] leading-none" aria-hidden="true"></fa-icon>
                </button>
              }
            </div>
          }
        }
      </div>
    } @else if (loading()) {
      <div class="m-6 flex gap-14" aria-hidden="true">
        @for (col of [0, 1, 2, 3]; track col) {
          <div class="flex flex-col gap-2.5">
            <div class="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
            <div class="h-14 w-52 bg-white border border-gray-200 rounded-lg animate-pulse"></div>
            @if (col > 1) { <div class="h-14 w-52 bg-white border border-gray-200 rounded-lg animate-pulse"></div> }
          </div>
        }
      </div>
    }
  </div>

  <!-- Aide clavier -->
  <div class="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 border-t border-gray-100 bg-gray-50 text-[11px] text-gray-500 shrink-0">
    <span><kbd class="font-sans">←↑↓→</kbd> se déplacer</span>
    <span><kbd class="font-sans">Entrée</kbd> ouvrir</span>
    <span><kbd class="font-sans">{{ isMac ? '⌘' : 'Ctrl' }}+Entrée</kbd> nouvel onglet</span>
    <span><kbd class="font-sans">Espace</kbd> recentrer</span>
    <span><kbd class="font-sans">⌫</kbd> centre précédent</span>
    <span><kbd class="font-sans">T</kbd> vue</span>
    <span><kbd class="font-sans">/</kbd> changer de fiche</span>
    <span class="ml-auto"></span>
    <app-palette-switcher active="graph" (switchTo)="onSwitch($event)" />
  </div>
</div>
